// src/find-match-rects.ts
// Computes match rectangles for RequestType.FIND_MATCH_RECTS (search-based redaction
// marking). Scans every page via getTextContent() — pages need not be rendered.
// Rects are returned in the stored-markup coordinate space: page-relative, scale 1
// (CSS px, 96 DPI), top-down origin, default page rotation (no orientation override) —
// i.e. page.getViewport({ scale: 96/72 }).
import { findPatternMatches, findPhraseMatches, matchContext, PhraseMatch } from './search-match';
import { MatchPattern, MatchRect, MatchRectsResponse } from './shared/model/docavea-viewer-interface';

const CSS_UNITS = 96.0 / 72.0;
const DESCENT_MARGIN = 0.2; // Fraction of line height the mark extends below the baseline (covers visible descenders on scanned/OCR pages).

export interface TextItemLike {
    str: string;
    transform: number[];
    width: number;
    height: number;
    fontName: string;
    hasEOL: boolean;
}

export interface PageTextIndex {
    pageText: string;
    spans: { start: number; end: number; itemIndex: number }[];
}

export interface MatchSegment {
    itemIndex: number;
    startInItem: number;
    endInItem: number;
}

type CharMeasure = (ch: string) => number;

// Minimal structural view of a pdfjs PageViewport — keeps segmentRect testable with a stub.
export interface ViewportLike {
    convertToViewportPoint(x: number, y: number): number[];
}

// keepPlacesApart (Redaction v2, visiodocs-app#493): also break between two items when the second does not continue the
// first on its line, unless the text already has a space there. pdf.js can list a line's items out of order with no separator ("Side 1" then "Henrik Hansen" in the
// court test document's header), and the whole-word expansion then took in the "1". v1 asks without it and joins as before.
export function buildPageTextIndex(items: TextItemLike[], keepPlacesApart = false): PageTextIndex {
    let pageText = '';
    const spans: PageTextIndex['spans'] = [];
    items.forEach((item, itemIndex) => {
        const previous = items[itemIndex - 1];
        if (keepPlacesApart && previous && !previous.hasEOL && !/\s$/.test(previous.str) && !/^\s/.test(item.str) && !continuesOnLine(previous, item)) {
            pageText += '\n';
        }
        spans.push({ start: pageText.length, end: pageText.length + item.str.length, itemIndex });
        pageText += item.str;
        if (item.hasEOL) {
            pageText += '\n'; // separator offset belongs to no item; the phrase regex treats it as whitespace
        }
    });
    return { pageText, spans };
}

// pdf.js's own limit for letter spacing (TRACKING_SPACE_FACTOR in its text extraction): a gap up to 0.102 x the font size
// is within a word, a wider one is a space. Relative to the font size, so 2 pt and 20 pt text are judged alike.
const LETTER_SPACING_MAX = 0.102;

// Whether `next` starts on `item`'s baseline, about where `item` ends: no earlier (half a line height of slack, for
// kerning and rounding), and no later than letter spacing reaches. Further right is another word or another place.
function continuesOnLine(item: TextItemLike, next: TextItemLike): boolean {
    const [a, b, , , e, f] = item.transform;
    const baseLen = Math.hypot(a, b);
    if (baseLen === 0 || item.str.length === 0 || next.str.length === 0) {
        return true; // nothing to measure by, or an empty item: join as before
    }
    const ux = a / baseLen;
    const uy = b / baseLen;
    const dx = next.transform[4] - e;
    const dy = next.transform[5] - f;
    const size = item.height || baseLen;
    const slack = 0.5 * size;
    const along = dx * ux + dy * uy;
    const across = dy * ux - dx * uy;
    return Math.abs(across) <= slack && along >= item.width - slack && along <= item.width + LETTER_SPACING_MAX * size;
}

export function matchToSegments(index: PageTextIndex, match: PhraseMatch): MatchSegment[] {
    const segments: MatchSegment[] = [];
    for (const span of index.spans) {
        if (span.start >= match.end) {
            break; // spans are sorted by offset
        }
        if (span.end <= match.start) {
            continue;
        }
        segments.push({
            itemIndex: span.itemIndex,
            startInItem: Math.max(match.start, span.start) - span.start,
            endInItem: Math.min(match.end, span.end) - span.start,
        });
    }
    return segments;
}

export function segmentFractions(str: string, startInItem: number, endInItem: number, measure: CharMeasure): { f0: number; f1: number } {
    let total = 0;
    let before = 0;
    let inside = 0;
    for (let i = 0; i < str.length; i++) {
        const w = measure(str[i]);
        total += w;
        if (i < startInItem) {
            before += w;
        } else if (i < endInItem) {
            inside += w;
        }
    }
    if (total === 0) {
        if (str.length === 0) {
            return { f0: 0, f1: 1 };
        }
        return { f0: startInItem / str.length, f1: endInItem / str.length };
    }
    return { f0: before / total, f1: (before + inside) / total };
}

export function segmentRect(item: TextItemLike, seg: MatchSegment, measure: CharMeasure, viewport: ViewportLike): MatchRect | null {
    const [a, b, , , e, f] = item.transform;
    const baseLen = Math.hypot(a, b);
    if (baseLen === 0 || item.width === 0) {
        return null;
    }
    const h = item.height || baseLen;
    const ux = a / baseLen; // unit vector along the baseline (PDF user space)
    const uy = b / baseLen;
    const vx = -uy;         // unit vector perpendicular, towards the ascender
    const vy = ux;
    const { f0, f1 } = segmentFractions(item.str, seg.startInItem, seg.endInItem, measure);
    const s0 = f0 * item.width;
    const s1 = f1 * item.width;
    const corners = [
        [e + ux * s0 - vx * DESCENT_MARGIN * h, f + uy * s0 - vy * DESCENT_MARGIN * h],
        [e + ux * s1 - vx * DESCENT_MARGIN * h, f + uy * s1 - vy * DESCENT_MARGIN * h],
        [e + ux * s1 + vx * h, f + uy * s1 + vy * h],
        [e + ux * s0 + vx * h, f + uy * s0 + vy * h],
    ].map(([x, y]) => viewport.convertToViewportPoint(x, y));
    const xs = corners.map((p: number[]) => p[0]);
    const ys = corners.map((p: number[]) => p[1]);
    const left = Math.min(...xs);
    const top = Math.min(...ys);
    return { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
}

// A pattern (Redaction v2, visiodocs-app#493) is searched instead of the query, and echoed so the app can tell this viewer knew it.
// keepPlacesApart: see buildPageTextIndex.
export async function findMatchRects(pdfDocument: any, query: string, pattern?: MatchPattern, keepPlacesApart = false): Promise<MatchRectsResponse> {
    const response: MatchRectsResponse = { query, matches: [], pagesTotal: pdfDocument.numPages, pagesFailed: 0 };
    if (pattern) {
        response.pattern = pattern;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    // Per-char width measurer per font family, relative widths only (scaled to item.width by
    // segmentFractions). Cached across pages so repeated fonts aren't re-measured every page.
    const measurers = new Map<string, CharMeasure>();
    const measurerFor = (fontFamily: string): CharMeasure => {
        let measure = measurers.get(fontFamily);
        if (!measure) {
            const widths = new Map<string, number>();
            measure = (ch: string) => {
                let w = widths.get(ch);
                if (w === undefined) {
                    ctx.font = `16px ${fontFamily}`;
                    w = ctx.measureText(ch).width;
                    widths.set(ch, w);
                }
                return w;
            };
            measurers.set(fontFamily, measure);
        }
        return measure;
    };
    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
        try {
            const page = await pdfDocument.getPage(pageNumber);
            const textContent = await page.getTextContent();
            const items: TextItemLike[] = textContent.items.filter((i: any) => typeof i.str === 'string');
            const index = buildPageTextIndex(items, keepPlacesApart);
            const matches = pattern ? findPatternMatches(index.pageText, pattern) : findPhraseMatches(index.pageText, query);
            if (matches.length === 0) {
                continue;
            }
            const viewport = page.getViewport({ scale: CSS_UNITS }); // default rotation — matches stored-markup space
            const measureFor = (item: TextItemLike): CharMeasure =>
                measurerFor(textContent.styles?.[item.fontName]?.fontFamily ?? 'sans-serif');
            for (const match of matches) {
                const rects: MatchRect[] = [];
                for (const seg of matchToSegments(index, match)) {
                    const rect = segmentRect(items[seg.itemIndex], seg, measureFor(items[seg.itemIndex]), viewport);
                    if (rect) {
                        rects.push(rect);
                    }
                }
                if (rects.length > 0) {
                    response.matches.push({ pageNumber, rects, pageWidth: viewport.width, pageHeight: viewport.height, ...matchContext(index.pageText, match) });
                }
            }
        } catch (err) {
            console.error(`findMatchRects: page ${pageNumber} failed`, err);
            response.pagesFailed++;
        }
    }
    return response;
}
